import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowUpRightDots, faCircleExclamation, faGift } from '@fortawesome/free-solid-svg-icons';
import { useSelectedBot } from '../contexts/SelectedBotContext.js';
import useAiCredits, { creditsOn } from '../hooks/useAiCredits';
import { useUnsavedChanges } from '../contexts/UnsavedChangesContext.js';
import { openLinkedInBonus, useLinkedInBonusOffer } from './LinkedInBonus';
import './BotUsageFooter.css';

const BASE = process.env.REACT_APP_API_BASE_URL;

// Pinned to the bottom of the sidebar: how many AI responses the active
// bot has used against its limit (plan allowance + purchased credits), plus
// the upgrade / billing CTA, and the LinkedIn bonus in gold while the account has not claimed it.
const BotUsageFooter = () => {
  const { t, i18n } = useTranslation();
  const bonusCredits = useLinkedInBonusOffer();
  const { selectedBot } = useSelectedBot();
  const navigate = useNavigate();
  const { requestUnsavedAction } = useUnsavedChanges();

  // Purchased extra-message credits aren't on the bot object (the list query
  // stays cheap), so pull the remaining balance per selected bot. Re-pulled
  // on `ub:usage-updated` (test chat / a purchase) so the meter stays current.
  const [purchased, setPurchased] = useState(0);
  const botId = selectedBot?.id;
  useEffect(() => {
    if (!botId) { setPurchased(0); return; }
    let alive = true;
    const pull = async () => {
      try {
        const res = await fetch(`${BASE}bots/${botId}/message-credits`);
        if (!res.ok) return;
        const json = await res.json();
        if (alive) setPurchased(Number(json.purchased_remaining || 0));
      } catch { /* non-critical sidebar enrichment */ }
    };
    pull();
    const onUsage = () => pull();
    window.addEventListener('ub:usage-updated', onUsage);
    return () => { alive = false; window.removeEventListener('ub:usage-updated', onUsage); };
  }, [botId]);

  // AI credits on (backend controller/credits): the credits of the plan and the extra credits
  // (packs, Free leftovers) against what is used; the unit reads "AI credits".
  const aiCredits = useAiCredits(botId);
  const inCredits = creditsOn(aiCredits);

  if (!selectedBot) return null;

  const creditLimit = inCredits ? Number(aiCredits.plan_allowance || 0) + Number(aiCredits.extra_remaining || 0) : 0;
  const used = inCredits
    ? Math.max(0, creditLimit - Number(aiCredits.available || 0))
    : Number(selectedBot.current_message_count ?? 0);
  const planLimit = Number(selectedBot.subscription_plan?.message_limit ?? 0);
  const limit = inCredits ? creditLimit : planLimit + purchased;
  const planName = selectedBot.subscription_plan?.name;
  // Subscription set to cancel at period end (still active until valid_until):
  // backend status_display gives className "ending" + text "Expires <date>".
  const isCancelling = selectedBot.status_display?.className === 'ending';
  const endsText = selectedBot.status_display?.text || '';
  // AI credits: what is left, and the bar shows the share that is left
  const left = inCredits ? Math.max(0, Number(aiCredits.available || 0)) : 0;
  const pct = inCredits
    ? (limit > 0 ? Math.min(100, Math.max(0, Math.round((left / limit) * 100))) : 0)
    : (limit > 0 ? Math.min(100, Math.max(0, Math.round((used / limit) * 100))) : 0);
  const atLimit = inCredits ? left <= 0 : limit > 0 && used >= limit;

  return (
    <div className="bot-usage-footer" data-credits={inCredits ? 'on' : 'off'}>
      <div className="bot-usage-head">
        <span className="bot-usage-label">{inCredits ? t('usage_ai_credits') : t('usage_ai_responses')}</span>
        {planName && <span className="bot-usage-plan">{planName}</span>}
      </div>

      <div className="bot-usage-count">
        {inCredits ? t('usage_credits_left', { count: left.toLocaleString('de-CH') }) : (
          <>
            {used.toLocaleString('de-CH')}
            {limit > 0 ? ` / ${limit.toLocaleString('de-CH')}` : ''}
            {purchased > 0 && (
              <span className="bot-usage-extra">
                +{purchased.toLocaleString('de-CH')} extra
              </span>
            )}
          </>
        )}
      </div>

      {isCancelling && (
        <div className="bot-usage-ending">
          <FontAwesomeIcon icon={faCircleExclamation} />
          <span>{t('billing_plan_cancelled')}{endsText ? ` · ${endsText}` : ''}</span>
        </div>
      )}

      {limit > 0 && (
        <div className="bot-usage-track">
          <div
            className={`bot-usage-fill${atLimit ? ' full' : ''}`}
            style={{ width: `${pct}%` }}
          />
        </div>
      )}

      {/* Always go to the agent's in-app billing page — never forward Wix bots
          straight to the Wix App Market. The billing page itself handles the
          Wix-managed vs Stripe routing per agent. */}
      <button
        type="button"
        className="bot-usage-upgrade"
        onClick={() => requestUnsavedAction(() => navigate(`/billing/${selectedBot.id}`))}
      >
        <FontAwesomeIcon icon={faArrowUpRightDots} />
        {t('usage_upgrade_billing')}
      </button>

      {bonusCredits != null && (
        <button type="button" className="bot-usage-bonus" onClick={() => openLinkedInBonus('sidebar')}>
          <FontAwesomeIcon icon={faGift} />
          {t('bonus_sidebar_cta', { credits: new Intl.NumberFormat(i18n.language || 'en').format(bonusCredits) })}
        </button>
      )}
    </div>
  );
};

export default BotUsageFooter;
